import {
  CloseOutlined,
  Place,
  RouteOutlined,
  SendOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  DialogContent,
  Drawer,
  FormControlLabel,
  IconButton,
  lighten,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { nanoid } from "@reduxjs/toolkit";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { APIActions } from "../../../api/actions";
import { clearCache } from "../../../api/cache";
import DialogAlert from "../../../classes/DialogAlert";
import HelpIconButton from "../../../components/buttons/HelpIconButton";
import NoDataContent from "../../../components/form/NoDataContent";
import useReceiptsUpload from "../../../hooks/useReceiptsUpload";
import useRouteContext from "../../../hooks/useRouteContext";
import {
  getPolicyCurrentValue,
  isFixedRoutePolicy,
} from "../../../pages/admin/route-policies/utils";
import { selectAccountCurrency } from "../../../store/features/accountSlice";
import { setError } from "../../../store/features/base/errorBaseSlice";
import {
  closeElement,
  selectModalInfo,
} from "../../../store/features/base/modalsSlice";
import { openSnackbar } from "../../../store/features/base/snackbarBaseSlice";
import { addExpense } from "../../../store/features/expensesSlice";
import formatAmount from "../../../utils/formatAmount";
import { routeInitialState } from "../../../utils/initialStates";
import { curr } from "../../../utils/more/currency_country";
import FormContent from "../../expenses/new/components/FormContent";
import FixedRoutesSelector from "../components/FixedRoutesSelector";
import RouteSection from "../components/route-section/RouteSection";
import { calculateRoute, formatRouteToApi } from "../utils";
import ModalHeader from "./components/ModalHeader";
import RightContent from "./components/RightContent";

const createExpense = async ({
  receipts = [],
  values = {},
  role,
  sendToApproval = false,
  routes = [],
  roundTrip,
}) => {
  const expenseId = nanoid(8);
  try {
    const responseData = await APIActions.expenses.add(
      {
        id: expenseId,
        ...formatRouteToApi({
          expenseData: values,
          routes,
          roundTrip,
          receipts,
        }),
        sendToApproval,
        type: "route",
      },
      role,
    );
    return {
      ok: true,
      expenseId,
      data: responseData,
    };
  } catch (error) {
    console.log(error);
    return { ok: false, error, expenseId, data: null };
  }
};

const ModalNewRoute = () => {
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();

  const { role } = useRouteContext();

  const currency = useSelector(selectAccountCurrency);

  const { open, payload } = useSelector((state) =>
    selectModalInfo(state, "modalNewRoute"),
  );

  const onClose = useCallback(
    () => dispatch(closeElement("modalNewRoute")),
    [],
  );

  const [calculating, setCalculating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roundTrip, setRoundTrip] = useState(false);
  const [routes, setRoutes] = useState(
    payload?.initialState?.routes || ["", ""],
  );

  const [values, setValues] = useState({
    ...routeInitialState,
    ...(payload?.initialState?.data || {}),
  });
  const [directionsResponse, setDirectionsResponse] = useState(null);

  const { receipts, onRemoveReceipt, onUploadFiles, setReceipts } =
    useReceiptsUpload();

  //utils
  const isFixedRouteMode = useMemo(
    () => isFixedRoutePolicy(values?.routePolicy),
    [values?.routePolicy],
  );

  const fixedRouteAmount = useMemo(
    () =>
      isFixedRouteMode
        ? parseFloat(getPolicyCurrentValue(values?.routePolicy) || 0)
        : 0,
    [values?.routePolicy, isFixedRouteMode],
  );

  const totalAmount = useMemo(() => {
    if (isFixedRouteMode) {
      // Rota predefinida: valor fixo do trajeto (x2 quando ida e volta).
      return fixedRouteAmount * (roundTrip ? 2 : 1);
    }
    return (
      parseFloat(values?.distance || 0) *
      parseFloat(values?.routePolicy?.currentRate || 0)
    );
  }, [
    values?.routePolicy,
    values?.distance,
    isFixedRouteMode,
    fixedRouteAmount,
    roundTrip,
  ]);

  const isOk = useMemo(
    () => Boolean(directionsResponse) && values?.routePolicy,
    [Boolean(directionsResponse), values?.routePolicy],
  );

  //refs
  const abortControllerRef = useRef(null);

  const handleClose = useCallback(() => {
    if (isOk || calculating) {
      DialogAlert.show({
        title: "Descartar alterações?",
        message:
          "Você tem alterações não salvas. Tem certeza de que deseja descartá-las?",
        actions: [
          {
            text: "Cancelar",
          },
          {
            main: true,
            text: "Descartar",
            onClick: onClose,
          },
        ],
      });
      return;
    }
    onClose();
  }, [isOk, calculating, onClose]);

  const resetState = () => {
    setReceipts([]);
    setValues((prev) => {
      const prevPolicy = prev?.routePolicy;
      // Não preservamos rotas fixas entre aberturas — elas vêm atreladas
      // aos pontos do trajeto. Para taxas por km, mantemos a última escolha.
      const preservedPolicy = isFixedRoutePolicy(prevPolicy)
        ? routeInitialState?.routePolicy
        : prevPolicy || routeInitialState?.routePolicy;
      return {
        ...routeInitialState,
        routePolicy: preservedPolicy,
      };
    });
    setRoutes(["", ""]);
    setDirectionsResponse(null);
    setRoundTrip(false);
    setLoading(false);
    setCalculating(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  useEffect(() => {
    if (open && payload?.initialState) {
      const { routes, data } = payload?.initialState || {};
      if (data) {
        setValues((prev) => ({
          ...prev,
          ...(data || {}),
        }));
        // Em cópias de rota predefinida preservamos o "ida e volta" (nas rotas
        // por km o ida e volta já vem embutido na distância copiada).
        if (isFixedRoutePolicy(data?.routePolicy)) {
          setRoundTrip(Boolean(data?.roundTrip));
        }
      }
      if (routes?.length) {
        setRoutes(routes || ["", ""]);
        getDirectionsResponse(routes || []);
      }
    }
  }, [open]);

  const getDirectionsResponse = async (array) => {
    if (array.every(Boolean)) {
      setCalculating(true);
      const { results, distance } = await calculateRoute(array);
      if (!results) return;
      if (roundTrip) {
        setValues((prev) => ({ ...prev, distance: parseFloat(distance) * 2 }));
      } else {
        setValues((prev) => ({ ...prev, distance: parseFloat(distance) }));
      }

      setDirectionsResponse(results);
      setCalculating(false);
    }
  };

  const applyFixedRoute = useCallback(async (policy) => {
    const point1 = policy?.preDefinedRoute?.point1 || "";
    const point2 = policy?.preDefinedRoute?.point2 || "";
    if (!point1 || !point2) return;
    setRoundTrip(false);
    setValues((prev) => ({ ...prev, routePolicy: policy }));
    const newRoutes = [point1, point2];
    setRoutes(newRoutes);
    setCalculating(true);
    const calc = await calculateRoute(newRoutes);
    if (calc?.results) {
      // Em rota fixa, "ida e volta" dobra apenas o valor: a distância
      // permanece a do trajeto calculado.
      setValues((prev) => ({
        ...prev,
        distance: parseFloat(calc.distance),
      }));
      setDirectionsResponse(calc.results);
    }
    setCalculating(false);
  }, []);

  const handleClearFixedRoute = useCallback(() => {
    setRoundTrip(false);
    setValues((prev) => ({
      ...prev,
      routePolicy: "",
      distance: "",
    }));
    setRoutes(["", ""]);
    setDirectionsResponse(null);
  }, []);

  const handleChangeValues = useCallback(
    (prop, value) => {
      // Quando o usuário troca a política para uma rota fixa,
      // sincronizamos os pontos do trajeto automaticamente.
      if (prop === "routePolicy" && isFixedRoutePolicy(value)) {
        applyFixedRoute(value);
        return;
      }
      setValues((prev) => ({ ...prev, [prop]: value }));
    },
    [applyFixedRoute],
  );

  useEffect(() => {
    // Em rotas fixas o toggle não mexe na distância, apenas no valor total.
    if (isFixedRouteMode) return;
    if (roundTrip) {
      setValues((prev) => ({
        ...prev,
        distance: parseFloat(values?.distance || 0) * 2,
      }));
    } else {
      setValues((prev) => ({
        ...prev,
        distance: parseFloat(values?.distance || 0) / 2,
      }));
    }
  }, [roundTrip]);

  const handleToggleRoundTrip = useCallback(() => {
    setRoundTrip((prev) => !prev);
  }, []);

  const handleReoderRoutes = useCallback(
    (response = {}) => {
      const startIndex = response.source.index;
      const endIndex = response.destination.index;
      let result = Array.from(routes);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      setRoutes([...result]);
      getDirectionsResponse([...result]);
    },
    [routes],
  );

  const handleUpdateRoute = useCallback((idx, value) => {
    setRoutes((prev) => {
      prev.splice(idx, 1, value);
      return [...prev];
    });
  }, []);

  const handleRevertRoutes = useCallback(() => {
    let reversed = Object.assign([], routes);
    reversed.reverse();
    setRoutes([...reversed]);
    getDirectionsResponse([...reversed]);
  }, [routes]);

  const handleCalcFunction = useCallback(
    () => getDirectionsResponse(routes),
    [routes],
  );
  const handleAddRoute = useCallback(() => {
    setRoutes((prev) => {
      prev.push("");
      return [...prev];
    });
  }, []);

  const handleRemoveRoute = useCallback((idx) => {
    setRoutes((prev) => {
      prev.splice(idx, 1);
      getDirectionsResponse([...prev]);
      return [...prev];
    });
  }, []);

  //Função de criar despesa a partir do botão 'Criar'
  const handleCreate = async () => {
    setLoading(true);
    const { ok, data, expenseId, error } = await createExpense({
      role,
      receipts,
      values,
      roundTrip,
      routes,
      sendToApproval: false,
    });
    if (ok) {
      dispatch(
        addExpense({
          role,
          data: {
            id: expenseId,
            ...data,
          },
        }),
      );
      dispatch(openSnackbar({ message: "Percurso criado" }));
      onClose();
      params.set("expensesFilter", "opened");
      setParams(params);
    } else {
      dispatch(setError({ title: "Erro ao criar percurso", error }));
    }
    setLoading(false);
  };

  //Função de enviar despesa
  const handleSend = async () => {
    setLoading(true);
    const { ok, data, expenseId, error } = await createExpense({
      role,
      receipts,
      values,
      roundTrip,
      routes,
      sendToApproval: true,
    });
    const returnedData = Array.isArray(data) ? data[0] || {} : {};
    if (ok && returnedData?.isOk) {
      clearCache("/expenses");
      if (["pending", "all"].includes(params.get("expensesFilter"))) {
        dispatch(
          addExpense({
            role,
            data: {
              id: expenseId,
              _id: returnedData?.expenseId,
              ...(returnedData?.expense || {}),
            },
          }),
        );
      }
      dispatch(openSnackbar({ message: "Percurso enviado" }));
      onClose();
    } else {
      dispatch(setError({ title: "Erro ao enviar percurso", error }));
    }
    setLoading(false);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      variant="temporary"
      transitionDuration={80}
      SlideProps={{
        onExited: resetState,
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: "transparent",
          },
        },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "60em",
          boxShadow: 3,
          borderRadius: 5,
          m: "10px",
          height: "calc(100% - 25px)",
          bgcolor: (t) => lighten(t.palette.elevation1.main, 0.7),
        },
      }}
    >
      {loading && <LinearProgress />}
      <ModalHeader onClose={handleClose} />

      <DialogContent sx={{ p: 0, display: "flex", alignItems: "flex-start" }}>
        <Box
          boxShadow={2}
          zIndex={10}
          flex={1}
          maxWidth={"28em"}
          height={"100%"}
          display={"flex"}
          flexDirection={"column"}
        >
          <Box flex={1} flexBasis={0} overflow={"scroll"} pb={10}>
            {isFixedRouteMode && (
              <Box px={2} pt={2}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  gap={1}
                  px={1.2}
                  py={0.8}
                  borderRadius={2}
                  bgcolor={(t) => lighten(t.palette.primary.main, 0.85)}
                  border={1}
                  borderColor={"primary.light"}
                >
                  <RouteOutlined color="primary" sx={{ fontSize: "1.2rem" }} />
                  <Box flex={1} minWidth={0}>
                    <Typography
                      fontSize={".78rem"}
                      color="primary.dark"
                      fontWeight={"600"}
                      lineHeight={1.1}
                    >
                      Rota predefinida
                    </Typography>
                    <Typography
                      fontSize={".85rem"}
                      fontWeight={"600"}
                      noWrap
                      color="text.primary"
                    >
                      {values?.routePolicy?.name}
                    </Typography>
                  </Box>
                  <Tooltip title="Trocar / limpar rota predefinida">
                    <IconButton
                      size="small"
                      onClick={handleClearFixedRoute}
                      disabled={loading}
                    >
                      <CloseOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            )}
            {!isFixedRouteMode && (
              <Box mt={-1} px={1.5}>
                <RouteSection
                  routes={routes}
                  onCalcFunction={handleCalcFunction}
                  onAddRoute={handleAddRoute}
                  onRemoveRoute={handleRemoveRoute}
                  onUpdateRoute={handleUpdateRoute}
                  onDragEnd={handleReoderRoutes}
                  onRevertRoutes={handleRevertRoutes}
                  roundTrip={roundTrip}
                  onToggleRoundTrip={handleToggleRoundTrip}
                  isEditable={!isFixedRouteMode}
                />
              </Box>
            )}

            {Boolean(directionsResponse) ? (
              <Box p={2}>
                <FormContent
                  isRoute
                  isEditable
                  disabled={loading}
                  distanceReadOnly={isFixedRouteMode}
                  routePolicyFixedOnly={isFixedRouteMode}
                  values={{ ...values, currency }}
                  role={role}
                  onChangeValue={handleChangeValues}
                />
              </Box>
            ) : (
              <>
                <NoDataContent
                  iconSize="4rem"
                  Icon={Place}
                  height={180}
                  subtitle={
                    isFixedRouteMode
                      ? "Calculando rota..."
                      : "Selecione a rota para ver mais opções"
                  }
                />
                {!isFixedRouteMode && (
                  <FixedRoutesSelector role={role} onSelect={applyFixedRoute} />
                )}
              </>
            )}
          </Box>
          <Collapse in={Boolean(directionsResponse)}>
            <Box p={2} boxShadow={3}>
              <Stack direction={"row"} alignItems={"flex-start"}>
                <Box flex={1}>
                  <Typography
                    fontSize={".95rem"}
                    fontWeight={"500"}
                    color="text.secondary"
                  >
                    Valor total:
                  </Typography>
                  <Typography fontSize={"1.3rem"} fontWeight={"600"}>
                    {curr(currency)} {formatAmount(totalAmount)}
                  </Typography>
                </Box>
                {isFixedRouteMode && (
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        disabled={loading}
                        checked={Boolean(roundTrip)}
                        onChange={handleToggleRoundTrip}
                      />
                    }
                    label="Ida e volta"
                  />
                )}
                <HelpIconButton size="medium" title={"Sobre o total"}>
                  {isFixedRouteMode ? (
                    <>
                      Rota predefinida: valor fixo de {currency}{" "}
                      {formatAmount(fixedRouteAmount)} para o trajeto
                      {roundTrip ? (
                        <> x 2 (ida e volta) = {formatAmount(totalAmount)}</>
                      ) : (
                        <>.</>
                      )}
                    </>
                  ) : (
                    <>
                      {currency}{" "}
                      {formatAmount(values?.routePolicy?.currentRate || 0)} x{" "}
                      {formatAmount(values?.distance || 0)} km = {currency}{" "}
                      {formatAmount(totalAmount)}
                    </>
                  )}
                </HelpIconButton>
              </Stack>
              {!values?.routePolicy && (
                <Typography
                  color="textSecondary"
                  fontStyle={"italic"}
                  variant="caption"
                >
                  Selecione uma taxa de percurso para prosseguir
                </Typography>
              )}
              <Stack mt={2} direction={"row"} alignItems={"center"} gap={1}>
                <Button
                  onClick={handleCreate}
                  disabled={loading || !isOk}
                  variant="contained"
                  disableElevation
                >
                  Criar
                </Button>
                <Button onClick={handleClose}>Cancelar</Button>
                <Box flex={1} />
                <Button
                  onClick={handleSend}
                  disabled={loading || !isOk}
                  variant="outlined"
                  endIcon={<SendOutlined />}
                >
                  Enviar
                </Button>
              </Stack>
            </Box>
          </Collapse>
        </Box>
        <RightContent
          receipts={receipts}
          calculating={calculating}
          directionsResponse={directionsResponse}
          onUploadFiles={onUploadFiles}
          onRemoveReceipt={onRemoveReceipt}
        />
      </DialogContent>
    </Drawer>
  );
};

export default memo(ModalNewRoute);
